import React, { useState, useEffect, useRef } from 'react';
import { FileText, AlertCircle, Download, ZoomIn, ZoomOut, RotateCw, ChevronLeft, ChevronRight } from 'lucide-react';
import * as pdfjs from 'pdfjs-dist';

// Define the interfaces
interface PDFViewerProps {
  pdfUrl?: string;
  title?: string;
  subPath?: string;
}

const PDFViewer: React.FC<PDFViewerProps> = ({ pdfUrl, title = "Document", subPath }) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [pdfDocument, setPdfDocument] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [zoom, setZoom] = useState<number>(1);
  const [rotation, setRotation] = useState<number>(0);
  const [fallbackMode, setFallbackMode] = useState<boolean>(false);
  
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const API_URL = process.env.REACT_APP_API_URL || '';

  // Get the correct file URL based on environment
  const getFileUrl = () => {
    if (!pdfUrl) return '';
    console.log("PDF URL:", pdfUrl);
    
    
    // Check if pdfUrl is already a full URL
    if (pdfUrl.startsWith('http://') || pdfUrl.startsWith('https://')) {
      return pdfUrl;
    }
    
    // Extract filename without extension if needed
    const pdfFileName = pdfUrl.endsWith('.pdf') 
      ? pdfUrl.substring(0, pdfUrl.length - 4) 
      : pdfUrl;
    
    // Check if we're running on localhost
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    
    if (isLocalhost) {
      // Use local path for localhost
      console.log(API_URL+ pdfFileName + ".pdf");
      
      return `${API_URL}${pdfFileName}.pdf`;
    } else if (window.location.hostname.includes('naecoaching.com')) {
      // For naecoaching.com domain, use the naendapi URL
      return `https://naendapi.naecoaching.com/media/${subPath || 'docs'}/${pdfFileName}.pdf`;
    } else {
      // Default fallback
      return `${API_URL}/media/${subPath || 'docs'}/${pdfFileName}.pdf`;
    }
  };

  // Function to get auth token from localStorage
  const getAuthToken = (): string | null => {
    return localStorage.getItem('token');
  };

  // Initialize PDF.js worker
  useEffect(() => {
    pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;
  }, []);

  // Load PDF document
  useEffect(() => {
    const fileUrl = getFileUrl();
    console.log("File URL:", fileUrl);
    
    
    if (!fileUrl) {
      setIsLoading(false);
      setError("No PDF available");
      return;
    }

    setIsLoading(true);
    setError(null);
    setFallbackMode(false);
    
    const fetchPdf = async () => {
      try {
        // Configure fetch options with auth token
        const token = getAuthToken();
        let fetchOptions: RequestInit = {
          headers: {
            'Accept': 'application/pdf',
            ...(token && { 'Authorization': `Token ${token}` })
          }
        };
        
        // Add CORS settings when needed
        if (window.location.hostname.includes('naecoaching.com') && 
            fileUrl.includes('naendapi.naecoaching.com')) {
          fetchOptions = { 
            ...fetchOptions,
            mode: 'cors',
            credentials: 'include',
          };
        }
        
        // Fetch the PDF
        const response = await fetch(fileUrl, fetchOptions);
        console.log("PDF fetch response:", response);
        
        
        if (!response.ok) {
          throw new Error(`Failed to fetch PDF: ${response.status} ${response.statusText}`);
        }
        
        // Get PDF as blob and convert to arrayBuffer
        const blob = await response.blob();
        const arrayBuffer = await blob.arrayBuffer();
        
        // Load with PDF.js
        const pdf = await pdfjs.getDocument({ data: arrayBuffer }).promise;
        setPdfDocument(pdf);
        setTotalPages(pdf.numPages);
        setCurrentPage(1);
        setIsLoading(false);
      } catch (err: any) {
        console.error("Error loading PDF:", err);
        setIsLoading(false);
        setError("Failed to load PDF. Switching to fallback mode.");
        setFallbackMode(true);
        
        // Create fallback iframe if containerRef is available
        if (containerRef.current) {
          createFallbackIframe(fileUrl);
        }
      }
    };
    
    fetchPdf();
  }, [pdfUrl, subPath]);

  // Create fallback iframe for direct PDF viewing
  const createFallbackIframe = (fileUrl: string) => {
    if (!containerRef.current) return;
    
    // Get auth token for URL parameters if needed
    const token = getAuthToken();
    let fallbackUrl = fileUrl;
    
    // Append token to URL if available
    if (token) {
      fallbackUrl = fileUrl.includes('?') 
        ? `${fileUrl}&token=${token}` 
        : `${fileUrl}?token=${token}`;
    }
    
    // Clear container contents
    while (containerRef.current.firstChild) {
      containerRef.current.removeChild(containerRef.current.firstChild);
    }
    
    // Create iframe container
    const iframeContainer = document.createElement('div');
    iframeContainer.className = 'pdf-fallback w-full h-full';
    iframeContainer.innerHTML = `
      <iframe 
        src="${fallbackUrl}"
        style="width:100%; height:100%; border:0;" 
        title="PDF Document"
      ></iframe>
    `;
    
    containerRef.current.appendChild(iframeContainer);
  };

  // Render PDF page
  useEffect(() => {
    if (!pdfDocument || !canvasRef.current || fallbackMode) return;

    const renderPage = async () => {
      try {
        const page = await pdfDocument.getPage(currentPage);
        
        const viewport = page.getViewport({ 
          scale: zoom,
          rotation: rotation 
        });
        
        const canvas = canvasRef.current;
        if (!canvas) return;
        
        const context = canvas.getContext('2d');
        if (!context) {
          console.error("Unable to get 2D context");
          return;
        }
        
        canvas.height = viewport.height;
        canvas.width = viewport.width;
        
        const renderContext = {
          canvasContext: context,
          viewport: viewport
        };
        
        await page.render(renderContext).promise;
      } catch (err) {
        console.error("Error rendering page:", err);
        setError("Failed to render PDF page.");
      }
    };
    
    renderPage();
  }, [pdfDocument, currentPage, zoom, rotation, fallbackMode]);

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleZoomIn = () => {
    setZoom(Math.min(zoom + 0.25, 3));
  };

  const handleZoomOut = () => {
    setZoom(Math.max(zoom - 0.25, 0.25));
  };

  const handleRotate = () => {
    setRotation((rotation + 90) % 360);
  };

  const handleDownload = () => {
    const fileUrl = getFileUrl();
    if (fileUrl) {
      // Create download link with token if needed
      const token = getAuthToken();
      let downloadUrl = fileUrl;
      
      if (token) {
        downloadUrl = fileUrl.includes('?') 
          ? `${fileUrl}&token=${token}` 
          : `${fileUrl}?token=${token}`;
      }
      
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = title || 'document.pdf';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center w-full h-96 bg-gray-50 rounded-lg border border-gray-200">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        <p className="mt-4 text-gray-600">Loading PDF...</p>
      </div>
    );
  }

  if (error && !fallbackMode) {
    return (
      <div className="flex flex-col items-center justify-center w-full h-96 bg-gray-50 rounded-lg border border-gray-200">
        <div className="flex items-center justify-center h-16 w-16 rounded-full bg-gray-100">
          <AlertCircle className="h-8 w-8 text-gray-400" />
        </div>
        <p className="mt-4 text-gray-600 font-medium">{error}</p>
        <p className="mt-2 text-gray-500 text-sm">Please provide a valid PDF URL to display the document.</p>
        <button 
          onClick={() => setFallbackMode(true)}
          className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Try Fallback View
        </button>
      </div>
    );
  }

  return (
    <div className="w-full bg-white rounded-lg shadow-lg overflow-hidden">
      {/* Header */}
      <div className="bg-gray-800 text-white px-4 py-3 flex items-center justify-between">
        <div className="flex items-center">
          <FileText className="h-5 w-5 mr-2" />
          <h2 className="font-medium truncate">{title}</h2>
        </div>
        <div className="flex space-x-2">
          <button 
            onClick={handleDownload}
            className="p-1 rounded hover:bg-gray-700 transition-colors"
            title="Download PDF"
          >
            <Download className="h-5 w-5" />
          </button>
        </div>
      </div>
      
      {/* PDF Viewer */}
      <div 
        ref={containerRef}
        className="relative bg-gray-800 overflow-auto flex justify-center p-2 md:p-4 h-full"
      >
        {!fallbackMode && (
          <div className="pdf-container" style={{ 
            transformOrigin: 'center',
            transitionProperty: 'transform',
            transitionDuration: '0.3s',
            transitionTimingFunction: 'ease'
          }}>
            <canvas ref={canvasRef} className="shadow-lg w-full"></canvas>
          </div>
        )}
        {/* Fallback iframe will be injected here when needed */}
      </div>
      
      {/* Controls - Only show if not in fallback mode */}
      {!fallbackMode && (
        <div className="bg-gray-50 px-4 py-3 border-t border-gray-200 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <button 
                onClick={handleZoomOut}
                className="p-1 rounded hover:bg-gray-200 transition-colors"
                disabled={zoom <= 0.5}
                title="Zoom Out"
              >
                <ZoomOut className={`h-5 w-5 ${zoom <= 0.25 ? 'text-gray-400' : 'text-gray-600'}`} />
              </button>
              <span className="text-sm text-gray-600">{Math.round(zoom * 100)}%</span>
              <button 
                onClick={handleZoomIn}
                className="p-1 rounded hover:bg-gray-200 transition-colors"
                disabled={zoom >= 3}
                title="Zoom In"
              >
                <ZoomIn className={`h-5 w-5 ${zoom >= 3 ? 'text-gray-400' : 'text-gray-600'}`} />
              </button>
              <p>click any btn if no pdf view</p>
            </div>
            
            <button 
              onClick={handleRotate}
              className="p-1 rounded hover:bg-gray-200 transition-colors text-gray-600"
              title="Rotate"
            >
              <RotateCw className="h-5 w-5" />
            </button>
          </div>
          
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <button 
                onClick={handlePrevPage}
                className="p-1 rounded hover:bg-gray-200 transition-colors"
                disabled={currentPage <= 1}
                title="Previous Page"
              >
                <ChevronLeft className={`h-5 w-5 ${currentPage <= 1 ? 'text-gray-400' : 'text-gray-600'}`} />
              </button>
              <span className="text-sm text-gray-600">
                Page <span className="font-medium">{currentPage}</span> of <span className="font-medium">{totalPages}</span>
              </span>
              <button 
                onClick={handleNextPage}
                className="p-1 rounded hover:bg-gray-200 transition-colors"
                disabled={currentPage >= totalPages}
                title="Next Page"
              >
                <ChevronRight className={`h-5 w-5 ${currentPage >= totalPages ? 'text-gray-400' : 'text-gray-600'}`} />
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Fallback mode footer */}
      {fallbackMode && (
        <div className="bg-gray-50 px-4 py-3 border-t border-gray-200 flex items-center justify-center">
          <div className="text-sm text-gray-600">
            Using fallback PDF viewer
          </div>
        </div>
      )}
    </div>
  );
};

export default PDFViewer;